import React, { useMemo, useState } from 'react';
import { EM_FOLDERS, EM_TAGS, EM_TONE } from '../../email/mockData';
import { emThreadInFolder } from '../../email/utils';
import type { EmTagKey, EmThread } from '../../email/types';
import type { IconKind } from '../Icon/Icon';
import { Icon } from '../Icon/Icon';
import { useAgentHold } from '../../team-hold/teamHold';
import { EmailConvRow } from './EmailConvRow';

const EM_MAILBOXES = [
  { id: 'inbox', label: 'Inbox', icon: 'mail' as IconKind },
  { id: 'drafts', label: 'Drafts', icon: 'pencil' as IconKind },
  { id: 'sent', label: 'Sent', icon: 'send' as IconKind },
];

const filterEyebrow: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 9.5,
  fontWeight: 700,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: 'var(--ink-3)',
};

function EmailFilterChip({
  icon,
  label,
  on,
  color,
  bg,
  onClick,
}: {
  icon: IconKind;
  label: string;
  on: boolean;
  color: string;
  bg: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="em-fchip"
      data-on={on ? '1' : '0'}
      onClick={onClick}
      style={
        on
          ? {
              background: bg,
              color,
              borderColor: `color-mix(in srgb, ${color} 45%, transparent)`,
            }
          : undefined
      }
    >
      <Icon kind={icon} size={11} stroke={2.2} style={on ? { color } : { color: 'var(--ink-4)' }} />
      {label}
      {on ? <Icon kind="check" size={11} stroke={2.6} style={{ color, marginLeft: 1 }} /> : null}
    </button>
  );
}

export interface EmailInboxProps {
  activeId: string;
  onSelect: (id: string) => void;
  threads?: EmThread[];
}

export function EmailInbox({ activeId, onSelect, threads: threadsProp = [] }: EmailInboxProps) {
  const allThreads = threadsProp;
  const [state, setState] = useState('all');
  const [q, setQ] = useState('');
  const [starred, setStarred] = useState<Set<string>>(() => new Set());
  const [mailbox, setMailbox] = useState('inbox');
  const [mbOpen, setMbOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [selTags, setSelTags] = useState(() => new Set<EmTagKey>());
  const [selFolders, setSelFolders] = useState(() => new Set<string>());

  const toggleStar = (id: string) =>
    setStarred((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const toggleTag = (k: EmTagKey) =>
    setSelTags((s) => {
      const n = new Set(s);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });
  const toggleFolder = (k: string) =>
    setSelFolders((s) => {
      const n = new Set(s);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });
  const clearFilters = () => {
    setSelTags(new Set());
    setSelFolders(new Set());
  };
  const filterN = selTags.size + selFolders.size;

  const byMailbox = (t: EmThread) =>
    mailbox === 'inbox' ? true : mailbox === 'drafts' ? t.aiState === 'drafted_low_conf' : t.aiState === 'replied';

  const list = useMemo(() => {
    let r = allThreads.filter(byMailbox);
    if (state === 'unread') r = r.filter((t) => t.unread);
    if (state === 'mine') r = r.filter((t) => t.aiState === 'drafted_low_conf');
    if (state === 'starred') r = r.filter((t) => starred.has(t.id));
    if (selTags.size) r = r.filter((t) => t.tags.some((k) => selTags.has(k)));
    if (selFolders.size) r = r.filter((t) => [...selFolders].some((f) => emThreadInFolder(t, f)));
    if (q.trim()) {
      const s = q.toLowerCase();
      r = r.filter(
        (t) =>
          (t.subjectTr || t.subject).toLowerCase().includes(s) ||
          t.name.toLowerCase().includes(s) ||
          t.snippet.toLowerCase().includes(s),
      );
    }
    return [...r].sort(
      (a, b) => (b.tags.includes('emergency') ? 1 : 0) - (a.tags.includes('emergency') ? 1 : 0),
    );
  }, [allThreads, state, q, starred, mailbox, selTags, selFolders]);

  const mbCount = (id: string) =>
    allThreads.filter((t) =>
      id === 'inbox' ? true : id === 'drafts' ? t.aiState === 'drafted_low_conf' : t.aiState === 'replied',
    ).length;
  const inMb = allThreads.filter(byMailbox);
  const unreadN = inMb.filter((t) => t.unread).length;
  const mineN = inMb.filter((t) => t.aiState === 'drafted_low_conf').length;
  const cur = EM_MAILBOXES.find((m) => m.id === mailbox)!;
  const emailHeld = useAgentHold('email').held;

  return (
    <div
      className="flex min-h-0 shrink-0 flex-col border-r border-[var(--cream-2)] bg-[var(--surface-mute)]"
      style={{ width: 348 }}
    >
      {emailHeld ? (
        <div
          role="status"
          className="flex shrink-0 items-center gap-2 border-b border-[var(--cream-2)] bg-[var(--gold-tint)] px-4 py-2 text-[12px] font-semibold"
          style={{ color: '#9A6B1E' }}
        >
          <Icon kind="pause" size={13} stroke={2} />
          AI Email is on hold — your team answers
        </div>
      ) : null}
      <div className="relative z-[6] shrink-0 border-b border-[var(--cream-2)]" style={{ padding: '14px 16px 12px' }}>
        <div style={{ position: 'relative', marginBottom: 11 }}>
          <button
            type="button"
            className="em-mailbox"
            data-open={mbOpen ? '1' : '0'}
            onClick={() => {
              setMbOpen((o) => !o);
              setFilterOpen(false);
            }}
          >
            <span className="em-mailbox-ic">
              <Icon kind={cur.icon} size={15} stroke={1.9} />
            </span>
            <span
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 16,
                fontWeight: 600,
                letterSpacing: '-0.015em',
                color: 'var(--ink-0)',
              }}
            >
              {cur.label}
            </span>
            <span className="em-mailbox-cnt">{mbCount(mailbox)}</span>
            <Icon kind={mbOpen ? 'up' : 'down'} size={15} style={{ color: 'var(--ink-3)', marginLeft: 'auto' }} />
          </button>
          {mbOpen ? (
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 9 }} onClick={() => setMbOpen(false)} aria-hidden />
              <div className="em-mailbox-menu em-rise">
                {EM_MAILBOXES.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    className={`em-mailbox-opt ${mailbox === m.id ? 'active' : ''}`}
                    onClick={() => {
                      setMailbox(m.id);
                      setMbOpen(false);
                      setState('all');
                    }}
                  >
                    <Icon
                      kind={m.icon}
                      size={15}
                      stroke={1.9}
                      style={{ color: mailbox === m.id ? 'var(--sage-0)' : 'var(--ink-3)' }}
                    />
                    <span style={{ flex: 1, textAlign: 'left' }}>{m.label}</span>
                    <span className="em-mailbox-cnt">{mbCount(m.id)}</span>
                    {mailbox === m.id ? (
                      <Icon kind="check" size={14} stroke={2.4} style={{ color: 'var(--sage-0)' }} />
                    ) : null}
                  </button>
                ))}
              </div>
            </>
          ) : null}
        </div>

        <div
          style={{
            background: 'var(--cream-1)',
            border: '1px solid var(--cream-2)',
            borderRadius: 'var(--r-lg)',
            boxShadow: 'var(--sh-1)',
            overflow: 'hidden',
            position: 'relative',
            zIndex: 5,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '0 6px 0 13px',
              height: 40,
              borderBottom: '1px solid var(--cream-2)',
            }}
          >
            <Icon kind="search" size={14} style={{ color: 'var(--ink-4)' }} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search email…"
              style={{
                flex: 1,
                border: 0,
                background: 'transparent',
                outline: 'none',
                fontFamily: 'var(--font-body)',
                fontSize: 13,
                color: 'var(--ink-1)',
              }}
            />
            <button
              type="button"
              title="Filter by tags & folders"
              onClick={() => {
                setFilterOpen((o) => !o);
                setMbOpen(false);
              }}
              className="em-filter-btn"
              data-on={filterOpen || filterN > 0 ? '1' : '0'}
            >
              <Icon kind="filter" size={15} />
              {filterN > 0 ? <span className="em-filter-badge">{filterN}</span> : null}
            </button>
          </div>
          <div style={{ display: 'flex', gap: 4, padding: '7px 8px' }}>
            {(
              [
                ['all', 'All', inMb.length],
                ['unread', 'Unread', unreadN],
                ['starred', 'Starred', inMb.filter((t) => starred.has(t.id)).length],
                ['mine', 'Needs me', mineN],
              ] as const
            ).map(([k, l, n]) => (
              <button
                key={k}
                type="button"
                className={`em-state ${state === k ? 'active' : ''}`}
                onClick={() => setState(k)}
                style={{ flex: 1, justifyContent: 'center', padding: '6px 8px' }}
              >
                {l}
                {n > 0 ? (
                  <span
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 10,
                      fontWeight: 700,
                      color: k === 'unread' ? 'var(--ai-3)' : k === 'starred' ? 'var(--gold)' : 'var(--ink-3)',
                    }}
                  >
                    {n}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        </div>

        {filterOpen ? (
          <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 7 }} onClick={() => setFilterOpen(false)} aria-hidden />
            <div className="em-filter-pop em-rise">
              <div className="em-filter-hd">
                <span style={filterEyebrow}>Filter</span>
                {filterN > 0 ? (
                  <button type="button" className="em-filter-clear" onClick={clearFilters}>
                    Clear all ({filterN})
                  </button>
                ) : null}
              </div>
              <div className="em-filter-grp">
                <span style={{ width: 9, height: 9, borderRadius: 3, background: 'var(--grad-spectrum)', display: 'inline-block' }} />
                Tags
              </div>
              <div className="em-filter-chips">
                {(Object.keys(EM_TAGS) as EmTagKey[]).map((k) => {
                  const t = EM_TAGS[k];
                  const tn = EM_TONE[t.tone];
                  return (
                    <EmailFilterChip
                      key={k}
                      icon={t.icon}
                      label={t.label}
                      on={selTags.has(k)}
                      color={tn.fg}
                      bg={tn.bg}
                      onClick={() => toggleTag(k)}
                    />
                  );
                })}
              </div>
              <div className="em-filter-grp">
                <Icon kind="folder" size={11} style={{ color: 'var(--ink-4)' }} />
                Folders
              </div>
              <div className="em-filter-chips">
                {EM_FOLDERS.map((f) => (
                  <EmailFilterChip
                    key={f.id}
                    icon={f.icon as IconKind}
                    label={f.label}
                    on={selFolders.has(f.id)}
                    color="var(--sage-0)"
                    bg="var(--sage-tint, color-mix(in srgb, var(--sage-0) 14%, transparent))"
                    onClick={() => toggleFolder(f.id)}
                  />
                ))}
              </div>
            </div>
          </>
        ) : null}
      </div>

      <div className="cal-scroll em-list flex min-h-0 flex-1 flex-col overflow-y-auto">
        {list.map((t) => (
          <EmailConvRow
            key={t.id}
            thread={t}
            active={t.id === activeId}
            starred={starred.has(t.id)}
            onStar={() => toggleStar(t.id)}
            onClick={() => onSelect(t.id)}
          />
        ))}
        {list.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-4)', fontSize: 13 }}>No email matches.</div>
        ) : null}
      </div>
    </div>
  );
}
